import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import {
  useAbuseReports,
  useResolveAbuseReport,
} from '../../hooks/useAdminExtended';
import { api } from '../../api/client';
import type { AbuseReport } from '../../api/client';

const STATUS_TABS = [
  { label: 'Open', value: 'OPEN' },
  { label: 'Investigating', value: 'INVESTIGATING' },
  { label: 'Resolved', value: 'RESOLVED' },
  { label: 'Dismissed', value: 'DISMISSED' },
] as const;

function getTypeColor(
  type: string,
): 'error' | 'warning' | 'info' | 'default' {
  switch (type) {
    case 'spam':
      return 'error';
    case 'phishing':
      return 'error';
    case 'abuse':
      return 'warning';
    case 'bounce_rate':
      return 'info';
    default:
      return 'default';
  }
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

export default function AbuseReportsPage() {
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const { data: reports, isLoading, error } = useAbuseReports(statusFilter);
  const resolveMutation = useResolveAbuseReport();

  const [suspendDialog, setSuspendDialog] = useState<AbuseReport | null>(null);
  const [suspending, setSuspending] = useState(false);

  const handleResolve = async (reportId: string, resolution: string) => {
    await resolveMutation.mutateAsync({ id: reportId, resolution });
  };

  const handleSuspendHost = async (report: AbuseReport) => {
    if (!report.hostId) return;
    setSuspending(true);
    try {
      await api.admin.toggleActive(report.hostId);
      await resolveMutation.mutateAsync({
        id: report.id,
        resolution: 'RESOLVED',
      });
      setSuspendDialog(null);
    } catch {
      // Error is handled by the mutation
    } finally {
      setSuspending(false);
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load abuse reports.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ mb: 2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
      >
        Abuse Reports
      </Typography>

      <Tabs
        value={statusFilter}
        onChange={(_e, v) => setStatusFilter(v as string)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {STATUS_TABS.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            sx={{ minHeight: 44 }}
          />
        ))}
      </Tabs>

      {isLoading ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={140} />
          ))}
        </Stack>
      ) : reports && reports.length > 0 ? (
        <Stack spacing={2}>
          {reports.map((report) => (
            <Card key={report.id}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon
                      fontSize="small"
                      color={
                        report.reportType === 'spam' ||
                        report.reportType === 'phishing'
                          ? 'error'
                          : 'action'
                      }
                    />
                    <Chip
                      label={report.reportType}
                      size="small"
                      color={getTypeColor(report.reportType)}
                      sx={{ fontSize: 12, textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: 11, sm: 12 } }}
                  >
                    {formatDate(report.createdAt)}
                  </Typography>
                </Box>

                {report.hostId && (
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, fontSize: { xs: 13, sm: 14 } }}
                  >
                    <strong>Host:</strong> {report.hostId}
                  </Typography>
                )}
                {report.eventId && (
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, fontSize: { xs: 13, sm: 14 } }}
                  >
                    <strong>Event:</strong> {report.eventId}
                  </Typography>
                )}
                {report.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontSize: { xs: 13, sm: 14 } }}
                  >
                    {report.description}
                  </Typography>
                )}

                {report.resolvedAt && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1.5, fontSize: { xs: 11, sm: 12 } }}
                  >
                    Resolved: {formatDate(report.resolvedAt)}
                  </Typography>
                )}

                {(statusFilter === 'OPEN' ||
                  statusFilter === 'INVESTIGATING') && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    {statusFilter === 'OPEN' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() =>
                          handleResolve(report.id, 'INVESTIGATING')
                        }
                        disabled={resolveMutation.isPending}
                        sx={{ minHeight: 40, fontSize: { xs: 12, sm: 13 } }}
                      >
                        Investigate
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => handleResolve(report.id, 'RESOLVED')}
                      disabled={resolveMutation.isPending}
                      sx={{ minHeight: 40, fontSize: { xs: 12, sm: 13 } }}
                    >
                      Resolve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleResolve(report.id, 'DISMISSED')}
                      disabled={resolveMutation.isPending}
                      sx={{ minHeight: 40, fontSize: { xs: 12, sm: 13 } }}
                    >
                      Dismiss
                    </Button>
                    {report.hostId && (
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => setSuspendDialog(report)}
                        sx={{ minHeight: 40, fontSize: { xs: 12, sm: 13 } }}
                      >
                        Suspend Host
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 6 }}
        >
          No {statusFilter.toLowerCase()} reports.
        </Typography>
      )}

      {/* Suspend Host Confirmation Dialog */}
      <Dialog
        open={!!suspendDialog}
        onClose={() => setSuspendDialog(null)}
      >
        <DialogTitle>Suspend Host</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to suspend this host? They will be unable to
            log in or manage their events until reactivated.
          </Typography>
          {suspendDialog?.hostId && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Host ID: {suspendDialog.hostId}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSuspendDialog(null)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => suspendDialog && handleSuspendHost(suspendDialog)}
            disabled={suspending}
            sx={{ minHeight: 44 }}
          >
            Suspend Host
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
